import { useDimensions } from '../../hooks';
import styles from './LegacyTooltip.module.scss';

/**
 * Cardinal directions for positioning the `LegacyTooltip`.
 *
 * **Do not use this type to position `Tooltip`**
 *
 * @deprecated `LegacyTooltip` is deprecated in favor of `Tooltip`.
 */
export type TooltipPosition =
  | 'northwest'
  | 'north'
  | 'northeast'
  | 'east'
  | 'southeast'
  | 'south'
  | 'southwest'
  | 'west';

type Props = {
  /** Tooltip expands to accomodate content if true. */
  allowMultiline?: boolean;
  /**
   * Indicates whether the content is hidden from screenreaders.
   * This is useful when the `Tooltip` has already been associated
   * to a node via `aria-describedby` to prevent screenreaders from
   * reading the content of the `Tooltip` twice.
   *
   * Note that some screenreaders like VoiceOver only read content
   * associated via `aria-describedby` when the element with that
   * attribute is interactive, like a `button` element.
   */
  'aria-hidden'?: boolean;
  /** The content to render inside of the Tooltip. */
  children: React.ReactNode;
  /** The position to place the tooltip relative to the parent element. */
  position?: TooltipPosition | null;
  /** Unique id, intended for use with `aria-describedby`. */
  id?: string;
  /** The z-index of the tooltip. */
  zIndex?: number;
};

// V5 line-height plus top and bottom padding
const singleLineHeightPlusPadding = 28;

/**
 * <span style="color: #a81227; font-weight: bold;">⚠️ This component is deprecated in favor of [Tooltip](https://zinnia-storybook.zapier.com/?path=/docs/display-tooltip--docs).</span>
 *
 * @deprecated Use `Tooltip` from `@zapier/design-system` instead.
 */
export const LegacyTooltip = ({
  children,
  allowMultiline = false,
  'aria-hidden': ariaHidden = undefined,
  id = undefined,
  position = 'south',
  zIndex = 2,
}: Props) => {
  /**
   * Get the height of the tooltip to determine if it has multiple lines.
   * The tooltip's content could only be one line but have the `allowMultiline` prop set to true,
   * so we need the height to know for sure if the tooltip has multiple lines.
   */
  const [ref, { height }] = useDimensions();
  const isMultiline = height > singleLineHeightPlusPadding;

  return (
    <span
      aria-hidden={ariaHidden}
      className={styles.tooltip}
      data-testid={`tooltip-${position}`}
      data-position={position}
      data-multiline={isMultiline}
      data-allow-multiline={allowMultiline}
      data-zds
      id={id}
      ref={ref}
      role="tooltip"
      style={{ zIndex }}
    >
      <span className={styles.content} data-multiline={allowMultiline} data-zds>
        {children}
      </span>
    </span>
  );
};

// Export position styles for use by TooltipWrapper
export const TooltipPositionStyles = {
  northwest: { bottom: `calc(100% + var(--zds-space-2))`, left: 0 },
  north: {
    bottom: `calc(100% + var(--zds-space-2))`,
    left: '50%',
    transform: 'translateX(-50%)',
  },
  northeast: { bottom: `calc(100% + var(--zds-space-2))`, right: 0 },
  east: {
    left: `calc(100% + var(--zds-space-2))`,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  southeast: { top: `calc(100% + var(--zds-space-2))`, right: 0 },
  south: {
    top: `calc(100% + var(--zds-space-2))`,
    left: '50%',
    transform: 'translateX(-50%)',
  },
  southwest: { top: `calc(100% + var(--zds-space-2))`, left: 0 },
  west: {
    right: `calc(100% + var(--zds-space-2))`,
    top: '50%',
    transform: 'translateY(-50%)',
  },
};
